(function () {
  const config = window.theme && window.theme.freeGift;

  if (!config || !config.enabled) {
    return;
  }

  const cartJsonUrl = `${window.Shopify.routes.root}cart.js`;
  const isTruthy = (value) => String(value || '').toLowerCase() === 'true';
  const normalizeCurrency = (value) => String(value || '').toUpperCase();
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class FreeGiftManager {
    constructor(freeGiftConfig) {
      this.config = freeGiftConfig;
      this.syncPromise = null;
      this.pendingSync = false;
      this.checkoutBypass = false;
      this.checkoutInFlight = false;
      this.syncTimer = null;
      this.giftDiscountUnavailableSignature = null;
    }

    withTimeout(promise, timeoutMs, message) {
      return Promise.race([
        promise,
        delay(timeoutMs).then(() => {
          throw new Error(message);
        })
      ]);
    }

    getStatusNodes() {
      return Array.from(document.querySelectorAll('[data-free-gift-status]'));
    }

    setStatus(message, isError = false) {
      this.getStatusNodes().forEach((node) => {
        node.textContent = message || '';
        node.classList.toggle('is-error', Boolean(message) && isError);
      });
    }

    clearStatusSoon(delayMs = 2600) {
      setTimeout(() => {
        this.setStatus('');
      }, delayMs);
    }

    setToggleBusy(isBusy) {
      document.querySelectorAll('.free-gift-toggle').forEach((container) => {
        container.classList.toggle('is-busy', isBusy);
      });

      document.querySelectorAll('[data-free-gift-toggle]').forEach((toggle) => {
        toggle.disabled = isBusy;
      });
    }

    setCheckoutBusy(trigger, isBusy) {
      if (!trigger) {
        return;
      }

      trigger.classList.toggle('is-loading', isBusy);
      trigger.setAttribute('aria-busy', isBusy ? 'true' : 'false');

      if (trigger.tagName !== 'A') {
        trigger.disabled = isBusy;
      }
    }

    async proceedToCheckout(trigger) {
      this.checkoutBypass = true;
      this.setCheckoutBusy(trigger, false);

      if (trigger.tagName === 'A') {
        window.location.href = trigger.href;
        return;
      }

      const form = trigger.closest('form');

      if (form && form.requestSubmit) {
        form.requestSubmit(trigger);
      } else if (form) {
        form.submit();
      }
    }

    init() {
      document.addEventListener('on:cart:add', () => this.scheduleSync('cart-add'));
      document.addEventListener('on:cart:change', () => {
        this.updateThresholdAnnouncement();
        this.scheduleSync('cart-change');
      });
      document.addEventListener('change', (event) => this.handleToggleChange(event));
      document.addEventListener('click', (event) => this.handleCheckoutClick(event));

      this.updateThresholdAnnouncement();
      this.scheduleSync('init', 250);
    }

    scheduleSync(reason, delay = 120) {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        this.syncCart(reason).catch((error) => {
          console.error('Free gift sync failed:', error);
        });
      }, delay);
    }

    async fetchCart() {
      const response = await fetch(cartJsonUrl, {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }

      return response.json();
    }

    async updateCart(payload) {
      const response = await fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      return response.json();
    }

    async addGift(quantity) {
      if (quantity <= 0) {
        return;
      }

      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: this.config.variantId,
              quantity,
              properties: {
                [this.config.lineItemProperty]: 'true',
                [this.config.lineItemRuleProperty]: this.config.ruleName
              }
            }
          ]
        })
      });

      const payload = await response.json();

      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || `Gift add failed: ${response.status}`);
      }
    }

    getGiftLines(cart) {
      return cart.items.filter((item) => item.variant_id === this.config.variantId);
    }

    getGiftQuantity(cart) {
      return this.getGiftLines(cart).reduce((sum, item) => sum + item.quantity, 0);
    }

    getDiscountSignature(discounts) {
      return (discounts || [])
        .map((discount) => [
          discount.title,
          discount.type,
          discount.amount,
          discount.total_allocated_amount
        ].filter(Boolean).join(':'))
        .sort()
        .join(',');
    }

    getGiftValidationSignature(cart) {
      const itemSignature = cart.items
        .filter((item) => item.variant_id !== this.config.variantId)
        .map((item) => [
          item.variant_id,
          item.quantity,
          item.final_line_price,
          this.getDiscountSignature(item.line_level_discount_allocations)
        ].join(':'))
        .sort()
        .join('|');
      const cartDiscountSignature = this.getDiscountSignature(cart.cart_level_discount_applications);

      return [
        this.getCartCurrency(cart),
        itemSignature,
        cartDiscountSignature
      ].join('||');
    }

    getCartCurrency(cart) {
      const shopifyCurrency = window.Shopify && window.Shopify.currency;

      return normalizeCurrency(
        cart.currency ||
        cart.currency_code ||
        this.config.cartCurrency ||
        (shopifyCurrency && shopifyCurrency.active)
      );
    }

    getCurrencyRate(cart) {
      const cartCurrency = this.getCartCurrency(cart);
      const shopCurrency = normalizeCurrency(this.config.shopCurrency || this.config.thresholdCurrency || 'AUD');
      const shopifyCurrency = window.Shopify && window.Shopify.currency;
      const shopifyCurrencyActive = normalizeCurrency(shopifyCurrency && shopifyCurrency.active);
      const shopifyCurrencyRate = Number.parseFloat(shopifyCurrency && shopifyCurrency.rate);

      if (!cartCurrency || cartCurrency === shopCurrency) {
        return 1;
      }

      if (shopifyCurrencyActive === cartCurrency && Number.isFinite(shopifyCurrencyRate) && shopifyCurrencyRate > 0) {
        return shopifyCurrencyRate;
      }

      return null;
    }

    getThresholdCents(cart) {
      const thresholdCurrency = normalizeCurrency(this.config.thresholdCurrency || 'AUD');
      const shopCurrency = normalizeCurrency(this.config.shopCurrency || thresholdCurrency);
      const cartCurrency = this.getCartCurrency(cart);

      if (thresholdCurrency === 'GBP') {
        if (String(this.config.marketId) !== '41434448097' || cartCurrency !== 'GBP') {
          return null;
        }

        return this.config.thresholdCents;
      }

      if (thresholdCurrency !== shopCurrency) {
        return null;
      }

      if (!cartCurrency || cartCurrency === thresholdCurrency) {
        return this.config.thresholdCents;
      }

      const rate = this.getCurrencyRate(cart);

      if (!rate) {
        return null;
      }

      return Math.round(this.config.thresholdCents * rate);
    }

    getCurrentCurrencyRate() {
      const shopifyCurrency = window.Shopify && window.Shopify.currency;
      const activeCurrency = normalizeCurrency(shopifyCurrency && shopifyCurrency.active);
      const rate = Number.parseFloat(shopifyCurrency && shopifyCurrency.rate);

      if (activeCurrency !== 'NZD' || !Number.isFinite(rate) || rate <= 0) {
        return null;
      }

      return rate;
    }

    getNzdThresholdAmount() {
      const rate = this.getCurrentCurrencyRate();

      if (!rate) {
        return null;
      }

      return Math.ceil((this.config.thresholdCents / 100) * rate);
    }

    updateThresholdAnnouncement() {
      const thresholdAmount = this.getNzdThresholdAmount();

      document.querySelectorAll('[data-free-gift-threshold-announcement]').forEach((node) => {
        if (!node) {
          return;
        }

        const currencyNode = node.querySelector('[data-free-gift-nzd-threshold]');

        if (currencyNode) {
          const currencyText = thresholdAmount ? ` / NZD $${thresholdAmount}` : '';
          if (currencyNode.textContent !== currencyText) {
            currencyNode.textContent = currencyText;
          }
          currencyNode.hidden = !thresholdAmount;
          return;
        }

        if (!thresholdAmount) {
          return;
        }

        if (node.dataset.freeGiftThresholdUpdated === String(thresholdAmount)) {
          return;
        }

        const currentText = node.innerHTML;

        if (!currentText || !/NZD\s*\$\d+/i.test(currentText)) {
          return;
        }

        node.innerHTML = currentText.replace(/NZD\s*\$\d+/i, `NZD $${thresholdAmount}`);
        node.dataset.freeGiftThresholdUpdated = String(thresholdAmount);
      });
    }

    getEligibleSubtotal(cart) {
      return cart.items.reduce((sum, item) => {
        if (item.variant_id === this.config.variantId) {
          return sum;
        }

        return sum + item.final_line_price;
      }, 0);
    }

    isOptedOut(cart) {
      return isTruthy(cart.attributes && cart.attributes[this.config.optOutAttribute]);
    }

    getDesiredGiftQuantity(cart) {
      const eligibleSubtotal = this.getEligibleSubtotal(cart);
      const thresholdCents = this.getThresholdCents(cart);
      const validationSignature = this.getGiftValidationSignature(cart);

      if (!thresholdCents) {
        return 0;
      }

      if (eligibleSubtotal < thresholdCents) {
        return 0;
      }

      if (this.isOptedOut(cart)) {
        return 0;
      }

      if (validationSignature === this.giftDiscountUnavailableSignature) {
        return 0;
      }

      return Math.floor(eligibleSubtotal / thresholdCents);
    }

    buildGiftUpdates(cart, targetQuantity) {
      const giftLines = this.getGiftLines(cart);
      const updates = {};
      let remaining = targetQuantity;
      let retainedQuantity = 0;
      let hasChanges = false;

      giftLines.forEach((line) => {
        const nextQuantity = remaining > 0 ? Math.min(line.quantity, remaining) : 0;
        updates[line.key] = nextQuantity;

        if (nextQuantity !== line.quantity) {
          hasChanges = true;
        }

        remaining -= nextQuantity;
        retainedQuantity += nextQuantity;
      });

      return {
        hasChanges,
        retainedQuantity,
        updates
      };
    }

    buildPaidGiftUpdates(cart) {
      const updates = {};
      let hasChanges = false;

      this.getGiftLines(cart).forEach((line) => {
        if (Number(line.final_line_price || 0) > 0) {
          updates[line.key] = 0;
          hasChanges = true;
        }
      });

      return {
        hasChanges,
        updates
      };
    }

    async removePaidGiftLinesIfNeeded() {
      const cart = await this.fetchCart();
      const paidGiftUpdates = this.buildPaidGiftUpdates(cart);

      if (!paidGiftUpdates.hasChanges) {
        if (this.getGiftQuantity(cart) > 0) {
          this.giftDiscountUnavailableSignature = null;
        }

        return false;
      }

      this.giftDiscountUnavailableSignature = this.getGiftValidationSignature(cart);
      await this.updateCart({
        updates: paidGiftUpdates.updates
      });
      console.warn('Free gift discount was not applied. The paid gift line was removed.');

      return true;
    }

    async syncCart(reason) {
      if (this.syncPromise) {
        this.pendingSync = true;
        return this.syncPromise;
      }

      this.syncPromise = this.performSync(reason);

      try {
        return await this.syncPromise;
      } finally {
        this.syncPromise = null;

        if (this.pendingSync) {
          this.pendingSync = false;
          this.scheduleSync('follow-up');
        }
      }
    }

    async performSync(reason) {
      const cart = await this.fetchCart();
      const eligibleSubtotal = this.getEligibleSubtotal(cart);
      const thresholdCents = this.getThresholdCents(cart);
      const optedOut = this.isOptedOut(cart);
      const shouldClearOptOut = thresholdCents && eligibleSubtotal < thresholdCents && optedOut;
      const desiredGiftQuantity = shouldClearOptOut ? 0 : this.getDesiredGiftQuantity(cart);
      const giftUpdates = this.buildGiftUpdates(cart, desiredGiftQuantity);
      const addQuantity = Math.max(desiredGiftQuantity - giftUpdates.retainedQuantity, 0);

      let mutated = false;

      if (giftUpdates.hasChanges || shouldClearOptOut) {
        const payload = {};

        if (giftUpdates.hasChanges) {
          payload.updates = giftUpdates.updates;
        }

        if (shouldClearOptOut) {
          payload.attributes = {
            [this.config.optOutAttribute]: ''
          };
        }

        await this.updateCart(payload);
        mutated = true;
      }

      if (addQuantity > 0) {
        await this.addGift(addQuantity);
        mutated = true;
      }

      if (desiredGiftQuantity > 0 || this.getGiftQuantity(cart) > 0) {
        const removedPaidGift = await this.removePaidGiftLinesIfNeeded();
        mutated = mutated || removedPaidGift;
      }

      if (mutated || reason === 'init') {
        document.dispatchEvent(new CustomEvent('on:cart:change', {
          bubbles: true,
          cancelable: false
        }));
      }

      return {
        desiredGiftQuantity,
        eligibleSubtotal,
        mutated
      };
    }

    async handleToggleChange(event) {
      const toggle = event.target.closest('[data-free-gift-toggle]');

      if (!toggle) {
        return;
      }

      this.setToggleBusy(true);
      this.setStatus('Updating gift preference...');

      if (!toggle.checked) {
        this.giftDiscountUnavailableSignature = null;
      }

      try {
        await this.withTimeout(this.updateCart({
          attributes: {
            [this.config.optOutAttribute]: toggle.checked ? 'true' : ''
          }
        }), this.config.toggleTimeoutMs, 'Gift preference update timed out');

        document.dispatchEvent(new CustomEvent('on:cart:change', {
          bubbles: true,
          cancelable: false
        }));

        this.scheduleSync('opt-out-change', 60);
        this.setStatus('Gift preference updated.');
        this.clearStatusSoon();
      } catch (error) {
        console.error('Gift opt-out update failed:', error);
        toggle.checked = !toggle.checked;
        this.setStatus('We could not update the gift preference. Please try again.', true);
        this.clearStatusSoon(3600);
        this.scheduleSync('opt-out-recover', 300);
      } finally {
        this.setToggleBusy(false);
      }
    }

    async handleCheckoutClick(event) {
      const trigger = event.target.closest('[data-free-gift-checkout]');

      if (!trigger || this.checkoutBypass || this.checkoutInFlight) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.checkoutInFlight = true;
      this.setCheckoutBusy(trigger, true);
      this.setStatus('Preparing your cart for checkout...');

      try {
        await this.withTimeout(this.syncCart('checkout'), this.config.syncTimeoutMs, 'Checkout sync timed out');
        this.setStatus('');
        await this.proceedToCheckout(trigger);
      } catch (error) {
        console.error('Checkout sync failed:', error);
        this.setStatus('Gift sync took too long. Continuing to checkout.');
        this.clearStatusSoon();
        await this.proceedToCheckout(trigger);
      } finally {
        setTimeout(() => {
          this.setCheckoutBusy(trigger, false);
          this.checkoutBypass = false;
          this.checkoutInFlight = false;
        }, 800);
      }
    }
  }

  new FreeGiftManager(config).init();
})();
